import React, { useMemo, useState } from 'react';
import type { LscArchiveItem } from '../../types';
import { useTeamVisualResolver } from '../../hooks/useTeamVisualResolver';
import { formatArchiveMatchup, normalizeTwitchThumbnailUrl } from './leagueHomeUtils';
import TeamVisualBadge from './TeamVisualBadge';

export type ArchiveThumbnailProps = {
  archive: LscArchiveItem;
  compact?: boolean;
};

const ArchiveThumbnail: React.FC<ArchiveThumbnailProps> = ({ archive, compact = false }) => {
  const [failed, setFailed] = useState(false);
  const resolveVisual = useTeamVisualResolver();
  const thumbnailUrl = useMemo(
    () => normalizeTwitchThumbnailUrl(archive.thumbnail_url),
    [archive.thumbnail_url]
  );
  const matchup = formatArchiveMatchup(archive.match_summary);
  const awayVisual = resolveVisual({ label: archive.match_summary?.away_side_label ?? '' });
  const homeVisual = resolveVisual({ label: archive.match_summary?.home_side_label ?? '' });

  if (thumbnailUrl && !failed) {
    return (
      <img
        src={thumbnailUrl}
        alt=""
        className="h-full w-full object-cover"
        onError={() => setFailed(true)}
        data-testid="archive-replay-thumbnail"
      />
    );
  }

  return (
    <div
      className="flex h-full w-full flex-col items-center justify-center gap-2 bg-gradient-to-br from-secondary-900 via-secondary-800 to-primary-950 px-3 text-center"
      data-testid="archive-replay-thumbnail-placeholder"
    >
      <div className="flex items-center gap-2">
        <TeamVisualBadge visual={awayVisual} size={compact ? 'sm' : 'md'} />
        <span className="text-[10px] font-semibold uppercase tracking-wider text-secondary-500">
          vs
        </span>
        <TeamVisualBadge visual={homeVisual} size={compact ? 'sm' : 'md'} />
      </div>
      <p className="text-xs font-medium text-secondary-200 line-clamp-2">{matchup}</p>
      <p className="text-[10px] uppercase tracking-wide text-secondary-500">Replay</p>
    </div>
  );
};

export default ArchiveThumbnail;
